import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, X, LayoutGrid, Tag } from 'lucide-react'

// Bump the version suffix if we want to re-announce a future feature set.
const STORAGE_KEY = 'avi_dashboard_new_features_valuemap_brandvalue_v1'

/**
 * One-time announcement bubble introducing the two new dashboard tabs (Value Map + Brand Value).
 * Sits just under the tab bar and points up at the new tabs. Dismissal is persisted in
 * localStorage so it shows only once per browser.
 */
const NewFeaturesAnnouncement: React.FC = () => {
  const [show, setShow] = useState(false)

  useEffect(() => {
    try { if (!localStorage.getItem(STORAGE_KEY)) setShow(true) } catch { /* localStorage unavailable — skip */ }
  }, [])

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* ignore */ }
    setShow(false)
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="relative z-30 mx-2 mt-2 max-w-md"
        >
          {/* Speech-bubble pointer, roughly under the Value Map / Brand Value tabs */}
          <div className="absolute -top-1.5 left-28 h-3 w-3 rotate-45 bg-white border-l border-t border-emerald-200" />
          <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-white via-emerald-50/40 to-blue-50/40 shadow-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">New on your dashboard</h3>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-start gap-2 text-xs text-gray-700">
                      <LayoutGrid className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span><span className="font-semibold">Value Map</span> — see which content themes &amp; formats drive the most EMV.</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-gray-700">
                      <Tag className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><span className="font-semibold">Brand Value</span> — track the brands appearing in your content, ranked by value.</span>
                    </div>
                  </div>
                  <button
                    onClick={dismiss}
                    className="mt-3 inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-600 to-blue-600 text-white text-xs font-semibold hover:from-emerald-700 hover:to-blue-700 transition-colors shadow-sm"
                  >
                    Got it
                  </button>
                </div>
              </div>
              <button onClick={dismiss} title="Dismiss" className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default NewFeaturesAnnouncement
